/* eslint-disable no-param-reassign */
import { type SchoolWithSettings } from '../../../models/school';
import { type SchoolSettings } from '../types/apiTypes';

export interface GeneratePasswordOptions {
    length?: number;
    ambiguous?: boolean;
    number?: boolean;
    minNumber?: number;
    uppercase?: boolean;
    minUppercase?: number;
    lowercase?: boolean;
    minLowercase?: number;
    special?: boolean;
    minSpecial?: number;
    maxSequential?: number;
    anyLetter?: boolean; // New option to require any letter (uppercase or lowercase)
    minAnyLetter?: number; // Minimum number of any letters required
    onlyNumeric?: boolean; // New option to allow only numeric passwords
    noSpaces?: boolean; // New option to disallow spaces in passwords
    maxLength?: number; // New option to enforce maximum password length
    noSingleCharacter?: boolean; // New option to disallow passwords with only one unique character
}

// Afer santization all options are required
interface SafePasswordOptions {
    length: number;
    ambiguous: boolean;
    number: boolean;
    minNumber: number;
    uppercase: boolean;
    minUppercase: number;
    lowercase: boolean;
    minLowercase: number;
    special: boolean;
    minSpecial: number;
    maxSequential: number;
    anyLetter: boolean;
    minAnyLetter: number;
    onlyNumeric: boolean;
    noSpaces: boolean;
    maxLength: number;
    noSingleCharacter: boolean;
}

enum PasswordValidationErrorType {
    Length = 'password-length',
    Number = 'password-contain-number',
    Uppercase = 'password-contain-uppercase',
    Lowercase = 'password-contain-lowercase',
    Special = 'password-contain-special',
    Sequential = 'password-contain-sequential',
    Spaces = 'password-contain-spaces', // New error type for spaces
    MaxLength = 'password-max-length', // New error type for exceeding max length
    SingleCharacter = 'password-single-character' // New error type for single character passwords
}

export interface PasswordValidationError {
    type: PasswordValidationErrorType;
    message: string;
}

export interface PasswordValidationResult {
    isValidPassword: boolean;
    errors: PasswordValidationError[];
}

// Based on https://github.com/bitwarden/jslib/blob/master/common/src/services/passwordGeneration.service.ts
export class PasswordGenerator {
    static readonly defaultOptions = {
        length: 8, //matches zoom minimum length lowest value.
        ambiguous: false,
        number: true,
        minNumber: 1,
        uppercase: true,
        minUppercase: 1,
        lowercase: true,
        minLowercase: 1,
        special: true,
        minSpecial: 1,
        maxSequential: 14,
        anyLetter: false,
        minAnyLetter: 0,
        onlyNumeric: false,
        noSpaces: false,
        maxLength: 99, // Default maximum length
        noSingleCharacter: true // Default value for noSingleCharacter
    } as SafePasswordOptions;
    /**
     * See @link https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0061483
     * Must be at least 8 characters
     * Cannot be longer than 99 characters
     * Have at least 1 letter (a, b, c...)
     * Have at least 1 number (1, 2, 3...)
     * Include both uppercase and lower case letters
     * Cannot contain only one character (i.e., "111111" or "aaaaaa")
     * Cannot contain consecutive characters (i.e., "123456" or "abcdef")
     * Cannot contain spaces (i.e., "Go Zoom")
     */

    public positionsString: string = '';

    /**
     * Combines the defaultOptions with options and then sets the appropriate minNumber, minUppercase, minLowercase, minSpecial
     * based on the options provided.  This is to ensure that the password meets the requirements of the options.
     *
     * @param options GeneratePasswordOptions
     * @returns
     */
    private getSafeOptions(options: GeneratePasswordOptions): SafePasswordOptions {
        const opts = { ...PasswordGenerator.defaultOptions, ...options };
        if (opts.length === 0) {
            // When length is 0 then all the other values can be false or zero
            return {
                length: 0,
                ambiguous: false,
                number: false,
                minNumber: 0,
                uppercase: false,
                minUppercase: 0,
                lowercase: false,
                minLowercase: 0,
                special: false,
                minSpecial: 0,
                maxSequential: 0,
                anyLetter: false,
                minAnyLetter: 0,
                onlyNumeric: false,
                noSpaces: false,
                maxLength: 0,
                noSingleCharacter: true
            };
        }
        if (opts.length === undefined || opts.length === null) {
            opts.length = PasswordGenerator.defaultOptions.length;
        }
        if (!opts.number) {
            opts.minNumber = 0;
        }
        if (!opts.uppercase) {
            opts.minUppercase = 0;
        }
        if (!opts.lowercase) {
            opts.minLowercase = 0;
        }
        if (!opts.special) {
            opts.minSpecial = 0;
        }
        if (!opts.anyLetter) {
            opts.minAnyLetter = 0;
        }

        if (opts.onlyNumeric) {
            opts.lowercase = false;
            opts.uppercase = false;
            opts.anyLetter = false;
            opts.special = false;
            opts.minLowercase = 0;
            opts.minUppercase = 0;
            opts.minAnyLetter = 0;
            opts.minSpecial = 0;
        }

        this.sanitizePasswordLength(opts);

        return opts as SafePasswordOptions;
    }

    /**
     *
     * @param password The password to validate
     * @param options GeneratePasswordOptions
     * returns PasswordValidationResult inclduing an array of errors if there are any and the boolean isValidPassword
     * Each error contains a type and a message (to be used with the locallized string based on the type)
     */

    validatePassword(password: string, options: GeneratePasswordOptions): PasswordValidationResult {
        const opts = this.getSafeOptions(options);
        const errors: PasswordValidationError[] = [];

        // opt.length is set during getSafeOptions
        if (password.length < opts.length) {
            errors.push({
                type: PasswordValidationErrorType.Length,
                message: `${opts.length}`
            });
        }

        if (opts.number && (password.match(/\d/g)?.length || 0) < opts.minNumber) {
            errors.push({
                type: PasswordValidationErrorType.Number,
                message: `${opts.minNumber}`
            });
        }

        if (opts.uppercase && (password.match(/[A-Z]/g)?.length || 0) < opts.minUppercase) {
            errors.push({
                type: PasswordValidationErrorType.Uppercase,
                message: `${opts.minUppercase}`
            });
        }

        if (opts.lowercase && (password.match(/[a-z]/g)?.length || 0) < opts.minLowercase) {
            errors.push({
                type: PasswordValidationErrorType.Lowercase,
                message: `${opts.minLowercase}`
            });
        }

        if (opts.special && (password.match(/[!@#$%^&*]/g)?.length || 0) < opts.minSpecial) {
            errors.push({
                type: PasswordValidationErrorType.Special,
                message: `${opts.minSpecial}`
            });
        }

        if (opts.anyLetter && (password.match(/[a-zA-Z]/g)?.length || 0) < opts.minAnyLetter) {
            errors.push({
                type: PasswordValidationErrorType.Lowercase, // Reuse existing type for letters
                message: `${opts.minAnyLetter}`
            });
        }

        if (opts.maxSequential && this.hasSequentialCharacters(password, opts.maxSequential)) {
            errors.push({
                type: PasswordValidationErrorType.Sequential,
                message: `${opts.maxSequential}`
            });
        }

        if (opts.onlyNumeric && !/^\d+$/.test(password)) {
            errors.push({
                type: PasswordValidationErrorType.Number,
                message: 'Password must contain only numeric characters'
            });
        }

        if (opts.noSpaces && /\s/.test(password)) {
            errors.push({
                type: PasswordValidationErrorType.Spaces,
                message: 'Password must not contain spaces'
            });
        }

        if (opts.maxLength && password.length > opts.maxLength) {
            errors.push({
                type: PasswordValidationErrorType.MaxLength,
                message: `Password must not exceed ${opts.maxLength} characters`
            });
        }

        if (opts.noSingleCharacter && new Set(password).size === 1) {
            errors.push({
                type: PasswordValidationErrorType.SingleCharacter,
                message: 'Password must not contain only one unique character'
            });
        }

        return {
            isValidPassword: errors.length === 0,
            errors
        };
    }

    private hasSequentialCharacters(password: string, maxSequential: number): boolean {
        const alphanumericSequence = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const alphanumericSequenceReverse = alphanumericSequence.split('').reverse().join('');
        const sequences = [alphanumericSequence, alphanumericSequenceReverse];

        for (const sequence of sequences) {
            for (let i = 0; i <= sequence.length - maxSequential; i++) {
                const segment = sequence.slice(i, i + maxSequential);
                if (password.includes(segment)) {
                    return true;
                }
            }
        }
        return false;
    }

    generatePassword(options?: GeneratePasswordOptions): string {
        if (options?.length === 0) {
            return ''; // Return empty string if length is 0
        }
        const opts = this.getSafeOptions(options || {});

        let password = '';
        do {
            password = this.generateRawPassword(opts);
        } while (
            (opts.noSpaces && /\s/.test(password)) ||
            (opts.noSingleCharacter && new Set(password).size === 1) // Regenerate if only one unique character
        );

        return password;
    }

    private generateRawPassword(opts: SafePasswordOptions): string {
        if (opts.onlyNumeric) {
            return Array.from({ length: opts.length }, () => '0123456789'.charAt(Math.floor(Math.random() * 10))).join(
                ''
            );
        }

        const positions: string[] = [];
        if (opts.anyLetter && opts.minAnyLetter > 0) {
            for (let i = 0; i < opts.minAnyLetter; i++) {
                positions.push('a'); // 'a' for any letter
            }
        }
        if (opts.lowercase && opts.minLowercase > 0) {
            for (let i = 0; i < opts.minLowercase; i++) {
                positions.push('l');
            }
        }
        if (opts.uppercase && opts.minUppercase > 0) {
            for (let i = 0; i < opts.minUppercase; i++) {
                positions.push('u');
            }
        }
        if (opts.number && opts.minNumber > 0) {
            for (let i = 0; i < opts.minNumber; i++) {
                positions.push('n');
            }
        }
        if (opts.special && opts.minSpecial > 0) {
            for (let i = 0; i < opts.minSpecial; i++) {
                positions.push('s');
            }
        }
        while (positions.length < opts.length) {
            positions.push('a');
        }

        // shuffle
        PasswordGenerator.shuffleArray(positions);
        this.positionsString = positions.join('');
        // build out the char sets
        let allCharSet = '';

        let lowercaseCharSet = 'abcdefghijkmnopqrstuvwxyz';
        if (opts.ambiguous) {
            lowercaseCharSet += 'l';
        }
        if (opts.lowercase || opts.anyLetter) {
            allCharSet += lowercaseCharSet;
        }

        let uppercaseCharSet = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        if (opts.ambiguous) {
            uppercaseCharSet += 'IO';
        }
        if (opts.uppercase || opts.anyLetter) {
            allCharSet += uppercaseCharSet;
        }

        let numberCharSet = '23456789';
        if (opts.ambiguous) {
            numberCharSet += '01';
        }
        if (opts.number) {
            allCharSet += numberCharSet;
        }

        const specialCharSet = '!@#$%^&*';
        if (opts.special) {
            allCharSet += specialCharSet;
        }

        let password = '';
        while (password.length < positions.length) {
            let positionChars = allCharSet;
            let i = password.length;
            switch (positions[i]) {
                case 'l':
                    positionChars = lowercaseCharSet;
                    break;
                case 'u':
                    positionChars = uppercaseCharSet;
                    break;
                case 'n':
                    positionChars = numberCharSet;
                    break;
                case 's':
                    positionChars = specialCharSet;
                    break;
                case 'a':
                    positionChars = allCharSet;
                    break;
                default:
                    break;
            }
            password += positionChars.charAt(Math.floor(Math.random() * positionChars.length));
            password = this.sanitizeSequentialCharacters(password, positions.length, opts.maxSequential);
        }
        return password;
    }

    /*
     * Sanitize the password to ensure that the password does not contain sequential characters.
     * Sequential characters are defined as characters that are in sequence in the character set
     * that are either ascending or descnding, e.g. 'abc', '123', 'xyz', '321', 'zyx', etc, or
     * characters that are in qwerty keyboard sequence, e.g. 'qwe', '123', 'qaz', etc.
     * this is to prevent easily guessable passwords. It is unlikely that our randomization
     * will generate a password with sequential characters, but this is a final safety net to ensure
     * that the password doesn't break any zoom password rules.
     * @param password - The password to sanitize
     * @param maxSequential - The maximum number of sequential characters allowed
     * @returns The sanitized password
     */
    private sanitizeSequentialCharacters(password: string, passwordLength: number, maxSequential?: number): string {
        if (!maxSequential || maxSequential >= passwordLength) {
            return password;
        }
        // create sequences to block
        const alphanumericSequence = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const alphanumericSequenceReverse = alphanumericSequence.split('').reverse().join('');
        const qwertySequence = 'qwertyuiopasdfghjklzxcvbnm';
        const qwertySequenceReverse = qwertySequence.split('').reverse().join('');
        const sequences = [alphanumericSequence, alphanumericSequenceReverse, qwertySequence, qwertySequenceReverse];
        // check for sequential characters
        let sanitizedPassword = password;
        sequences.forEach((sequence) => {
            for (let i = 0; i < sequence.length - maxSequential; i++) {
                const sequenceToCheck = sequence.slice(i, i + maxSequential);
                if (sanitizedPassword.includes(sequenceToCheck)) {
                    sanitizedPassword = this.sanitizeSequentialCharacters(sanitizedPassword, maxSequential);
                }
            }
        });
        return sanitizedPassword;
    }

    // ref: https://stackoverflow.com/a/12646864/1090359
    private static shuffleArray(array: string[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));

            // eslint-disable-next-line no-param-reassign
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    private sanitizePasswordLength(opt: SafePasswordOptions): void {
        let minUppercaseCalc: number = opt.minUppercase;
        let minLowercaseCalc: number = opt.minLowercase;
        let minNumberCalc: number = opt.minNumber;
        let minSpecialCalc: number = opt.minSpecial;
        let minAnyLetterCalc: number = opt.minAnyLetter;

        if (opt.anyLetter && opt.minAnyLetter <= 0) {
            minAnyLetterCalc = 1;
        } else if (!opt.anyLetter) {
            minAnyLetterCalc = 0;
        }

        if (opt.uppercase && opt.minUppercase <= 0) {
            minUppercaseCalc = 1;
        } else if (!opt.uppercase) {
            minUppercaseCalc = 0;
        }

        if (opt.lowercase && opt.minLowercase <= 0) {
            minLowercaseCalc = 1;
        } else if (!opt.lowercase) {
            minLowercaseCalc = 0;
        }

        if (opt.number && opt.minNumber <= 0) {
            minNumberCalc = 1;
        } else if (!opt.number) {
            minNumberCalc = 0;
        }

        if (opt.special && opt.minSpecial <= 0) {
            minSpecialCalc = 1;
        } else if (!opt.special) {
            minSpecialCalc = 0;
        }

        // This should never happen but is a final safety net
        if (!opt.length || opt.length < 1) {
            opt.length = 10;
        }

        const minLength: number =
            minUppercaseCalc + minLowercaseCalc + minNumberCalc + minSpecialCalc + minAnyLetterCalc;
        if (opt.length < minLength) {
            opt.length = minLength;
        }

        if (opt.maxLength && opt.length > opt.maxLength) {
            opt.length = opt.maxLength; // Ensure length does not exceed maxLength
        }

        opt.minUppercase = minUppercaseCalc;
        opt.minLowercase = minLowercaseCalc;
        opt.minNumber = minNumberCalc;
        opt.minSpecial = minSpecialCalc;
        opt.minAnyLetter = minAnyLetterCalc;
    }
}

/**
 * Special password rules for special clients go here--some clients
 * have unique Zoom password requirements.  In this future we will add support for this in config settings.
 * @param prefix - The hostname of the school in their class URL.
 */

export const getPasswordOptionsForPrefix = (prefix: string): GeneratePasswordOptions => {
    switch (prefix) {
        case 'docebo-zoom':
            // Docebo's corporate zoom instance only supports numeric passwords (this only applies to Docebo, not docebo lms integrations)
            return { length: 10, ambiguous: true, uppercase: false, lowercase: false, special: false, number: true };
        case 'vlc':
        case 'virtual':
            // NY DOE only allows numeric values with passcode length of 6 chars
            return { length: 6, ambiguous: true, uppercase: false, lowercase: false, special: false };
        case 'bcgu':
            // bcgu only allows numeric values with a lenght of 10 chars
            return { length: 10, ambiguous: true, uppercase: false, lowercase: false, special: false };
        case 'fit-monash':
            return { length: 10, ambiguous: true, uppercase: false, lowercase: false, special: false };
        case 'monashvirtualschool':
            return { length: 10, ambiguous: true, uppercase: false, lowercase: false, special: false };
        case 'bbgov':
            return { length: 10, ambiguous: true, uppercase: true, lowercase: true, special: true };
        default:
            return PasswordGenerator.defaultOptions;
    }
};

/**
 * Apply rules for meetings for customers with special meeeting requirement passcode rules.
 * @param schoolRecordWithSettings
 * @returns GeneratePasswordOptions from the school using the settings or the prefix as a fallback.
 */

export const generateMeetingPasswordSettingsForSchooolRecordWithSettings = (
    schoolRecordWithSettings: SchoolWithSettings | SchoolSettings
): GeneratePasswordOptions => {
    const zoomSettings = schoolRecordWithSettings.options.zoomSettings;
    if (zoomSettings?.useCustomPasscodeRequirements) {
        const customPasswordRequirements = {
            ambiguous: true,
            uppercase: false,
            lowercase: false,
            special: false
        } as GeneratePasswordOptions;

        if (zoomSettings?.minPasscodeLength === false) {
            customPasswordRequirements.length = 0;
            customPasswordRequirements.minNumber = 0;
        } else if (zoomSettings.passcodeLength) {
            customPasswordRequirements.length = zoomSettings.passcodeLength;
        } else {
            // use default value
            customPasswordRequirements.length = PasswordGenerator.defaultOptions.length;
        }

        if (zoomSettings?.requireNumber === true) {
            customPasswordRequirements.number = true;
            customPasswordRequirements.minNumber = 1;
        }
        if (zoomSettings?.requireLetter === true) {
            customPasswordRequirements.lowercase = true;
            customPasswordRequirements.uppercase = true;
        } else {
            customPasswordRequirements.lowercase = false;
            customPasswordRequirements.uppercase = false;
        }
        if (zoomSettings?.requireSpecialCharacter === true) {
            customPasswordRequirements.special = true;
            customPasswordRequirements.minSpecial = 1;
        } else {
            customPasswordRequirements.special = false;
        }

        if (zoomSettings?.requireUpperLowerCase === true) {
            customPasswordRequirements.minUppercase = 1;
            customPasswordRequirements.minLowercase = 1;
        }
        if (zoomSettings?.noConsecutiveCharacters === true) {
            customPasswordRequirements.maxSequential = zoomSettings.consecutiveCharacterLength;
        }
        if (zoomSettings.onlyNumericPasscode === true) {
            customPasswordRequirements.number = true;
            customPasswordRequirements.onlyNumeric = true;
            customPasswordRequirements.minNumber = 1;
            customPasswordRequirements.lowercase = false;
            customPasswordRequirements.uppercase = false;
            customPasswordRequirements.special = false;
        }
        return customPasswordRequirements;
    }

    return getPasswordOptionsForPrefix(schoolRecordWithSettings.prefix);
};
